import PropTypes from "prop-types";
import styled from "styled-components";
import { theme } from "../../../theme";
import Button from "../../Button/Button";
import Typography from "../../Typography";
import Modal from "../../Modal/Modal";

function BillingInvoicesModal({
	isOpen,
	onClose,
	closeLabel,
	t,
	billingInvoices,
	billingInvoicesHasMore,
	billingInvoicesTotal,
	loadingInvoiceId,
	onDownloadInvoice,
	formatInvoiceDate,
	formatInvoiceAmount,
	getInvoiceStatusLabel,
}) {
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t("settings.billing.invoices.title")}
			maxWidth="720px"
			maxHeight="90vh"
			overflowY="auto"
			closeLabel={closeLabel}
		>
			<InvoicesModalContent>
				<Typography
					as="p"
					$variant="body-2-medium"
					color={theme.colors.defaultWeak}
				>
					{t("settings.billing.invoices.modalDescription")}
				</Typography>
				{billingInvoicesHasMore && (
					<Typography
						as="p"
						$variant="body-2-medium"
						color={theme.colors.defaultWeak}
					>
						{t("settings.billing.invoices.truncatedHint", {
							count: billingInvoices.length,
							total: billingInvoicesTotal,
						})}
					</Typography>
				)}
				<InvoicesList>
					{billingInvoices.map((invoice) => (
						<InvoiceRow key={invoice.id}>
							<InvoiceRowCopy>
								<Typography as="span" $variant="body-1-semibold">
									{invoice.invoiceNumber}
								</Typography>
								<Typography
									as="span"
									$variant="body-2-medium"
									color={theme.colors.defaultWeak}
								>
									{invoice.invoicePeriod} ·{" "}
									{formatInvoiceDate(invoice.issuedAt)}
								</Typography>
							</InvoiceRowCopy>
							<InvoiceRowMeta>
								<InvoiceStatusBadge>
									{getInvoiceStatusLabel(invoice.status)}
								</InvoiceStatusBadge>
								<Typography as="span" $variant="body-2-semibold">
									{formatInvoiceAmount(invoice.total, invoice.currency)}
								</Typography>
								<Button
									size="m"
									$variant="outline"
									onClick={() => onDownloadInvoice(invoice.id)}
									disabled={loadingInvoiceId === invoice.id}
								>
									{loadingInvoiceId === invoice.id
										? t("settings.billing.invoices.downloading")
										: t("settings.billing.invoices.download")}
								</Button>
							</InvoiceRowMeta>
						</InvoiceRow>
					))}
				</InvoicesList>
			</InvoicesModalContent>
		</Modal>
	);
}

BillingInvoicesModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	closeLabel: PropTypes.string.isRequired,
	t: PropTypes.func.isRequired,
	billingInvoices: PropTypes.array.isRequired,
	billingInvoicesHasMore: PropTypes.bool.isRequired,
	billingInvoicesTotal: PropTypes.number.isRequired,
	loadingInvoiceId: PropTypes.string,
	onDownloadInvoice: PropTypes.func.isRequired,
	formatInvoiceDate: PropTypes.func.isRequired,
	formatInvoiceAmount: PropTypes.func.isRequired,
	getInvoiceStatusLabel: PropTypes.func.isRequired,
};

export default BillingInvoicesModal;

const InvoicesModalContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.sizing.md};
	padding: ${({ theme }) => theme.sizing.md};
`;

const InvoicesList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.sizing.sm};
`;

const InvoiceRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: ${({ theme }) => theme.sizing.sm};
	padding: ${({ theme }) => theme.sizing.md};
	border: 1px solid ${({ theme }) => theme.border.defaultWeak};
	border-radius: ${({ theme }) => theme.radius.sm};
	background: ${({ theme }) => theme.fill.defaultSubtle};

	@media (max-width: ${({ theme }) => theme.breakpoints.md}) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const InvoiceRowCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const InvoiceRowMeta = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.sizing.sm};
	flex-wrap: wrap;

	@media (max-width: ${({ theme }) => theme.breakpoints.md}) {
		width: 100%;
		justify-content: space-between;
	}
`;

const InvoiceStatusBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 6px 10px;
	border-radius: ${({ theme }) => theme.radius.pill || theme.radius.md};
	background: ${({ theme }) => theme.fill.defaultMain};
	border: 1px solid ${({ theme }) => theme.border.defaultWeak};
	color: ${({ theme }) => theme.colors.defaultWeak};
	font-size: 12px;
	font-weight: 600;
`;
